import { Customer, getCustomers, getNewSalesOrderFromData, getOrders, getOrderTypes, NonEmptyText, OrderType, SalesOrder, Tag } from '@acies/domain';
import { AutocompleteEvent, ButtonEvent, DropdownItem, InputField, InputFieldEvent, TagRendererEvent } from '@acies/ui';
import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

@Component({
	selector: 'app-orderslist-component',
	templateUrl: './orderslist.component.html',
	styleUrls: ['./orderslist.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class OrdersListComponent implements OnInit {
	@ViewChild('dialog') dialog: ElementRef<HTMLDialogElement>;
	@ViewChild('keyInputField') keyInputField: ElementRef<InputField>;
	@ViewChild('valueInputField') valueInputField: ElementRef<InputField>;

	private currentTagKey: string;
	private currentTagValue: string;
	private currentSalesOrder: SalesOrder | null;
	private router: Router;
	private selectedOrderType: null | OrderType;
	private selectedCustomer: Customer | null;
	public isContinueButtonEnabled: boolean;
	public isCreateTagButtonEnabled: boolean;
	public orders: SalesOrder[];
	public orderTypes: DropdownItem[];
	public debitors: DropdownItem[];
	public tags: Tag[];
	public constructor(router: Router) {
		this.router = router;
		this.currentTagKey = '';
		this.currentTagValue = '';
		this.currentSalesOrder = null;
		this.selectedOrderType = null;
		this.selectedCustomer = null;
		this.closeDialog = this.closeDialog.bind(this);
		this.showDialog = this.showDialog.bind(this);
		this.tagClosed = this.tagClosed.bind(this);
		this.isContinueButtonEnabled = false;
		this.isCreateTagButtonEnabled = false;
		this.orders = [];
		this.orderTypes = [];
		this.debitors = [];
		this.tags = [];
	}

	public ngOnInit(): void {
		this.orders = getOrders(40);
		this.orderTypes = this.#getDummyOrderTypesDropdownItems();
		this.debitors = this.#getDummyCustomersFropdownItems();
	}

	public orderTypeChanged(event: Event): void {
		if (event instanceof AutocompleteEvent) {
			if (event.selectedItem.value instanceof OrderType) {
				this.selectedOrderType = event.selectedItem.value;
				this.#updateIsContinueButtonEnabled();
			}
		}
	}

	public debitorChanged(event: Event): void {
		if (event instanceof AutocompleteEvent) {
			if (event.selectedItem.value instanceof Customer) {
				this.selectedCustomer = event.selectedItem.value;
				this.#updateIsContinueButtonEnabled();
			}
		}
	}

	public tagClosed(event: Event): void {
		if (event instanceof TagRendererEvent) {
			this.#removeTagFromTags(event.tag);
		}
	}

	public keyInputFieldChanged(event: Event): void {
		if (event instanceof InputFieldEvent) {
			this.currentTagKey = event.text;
			this.#updateIsCreateTagButtonEnabled();
		}
	}

	public valueInputFieldChanged(event: Event): void {
		if (event instanceof InputFieldEvent) {
			this.currentTagValue = event.text;
			this.#updateIsCreateTagButtonEnabled();
		}
	}

	public createTagButtonClicked(event: Event): void {
		if (event instanceof ButtonEvent) {
			this.#createAndAddNewTag();
			this.#resetInputFields();
			this.#resetCurrentInputValues();
			this.#updateIsCreateTagButtonEnabled();
		}
	}

	public continueButtonClicked(): void {
		this.currentSalesOrder = this.#getNewSalesOrderFromCurrentData();
		if (this.currentSalesOrder) {
			// prepend the newly created order to the orders Array
			this.orders = [this.currentSalesOrder, ...this.orders];
			// we keep all the data in the dialog, so the user doesn't need to
			// select the same OrderType, Debitor and Tags again.
			this.closeDialog();
			this.#openConfigurator();
		}
	}

	public cancelButtonClicked(): void {
		this.closeDialog();
	}

	public closeDialog(): void {
		this.dialog.nativeElement.close();
	}

	public orderDoubleClicked(order: SalesOrder): void {
		this.currentSalesOrder = order;
		this.#openConfigurator();
	}

	#getNewSalesOrderFromCurrentData(): SalesOrder | null {
		if (this.selectedCustomer && this.selectedOrderType) {
			return getNewSalesOrderFromData(this.selectedCustomer, this.selectedOrderType, this.tags);
		}
		return null;
	}

	#updateIsContinueButtonEnabled(): void {
		this.isContinueButtonEnabled = this.selectedCustomer !== null && this.selectedOrderType !== null;
	}

	#removeTagFromTags(tag: Tag): void {
		const index = this.tags.indexOf(tag);
		this.tags.splice(index, 1);
	}

	#createAndAddNewTag(): void {
		const key = new NonEmptyText(this.currentTagKey);
		const value = new NonEmptyText(this.currentTagValue);
		this.tags.push(new Tag({ key, value }));
	}

	#resetCurrentInputValues(): void {
		this.currentTagKey = '';
		this.currentTagValue = '';
	}

	#resetInputFields(): void {
		this.keyInputField.nativeElement.text = '';
		this.valueInputField.nativeElement.text = '';
	}

	#updateIsCreateTagButtonEnabled(): void {
		if (this.currentTagKey && this.currentTagValue) {
			this.isCreateTagButtonEnabled = true;
		} else {
			this.isCreateTagButtonEnabled = false;
		}
	}

	#getDummyOrderTypesDropdownItems(): DropdownItem[] {
		const dropdownItems: DropdownItem[] = [];
		const orderTypes = getOrderTypes();
		for (const type of orderTypes) {
			dropdownItems.push(new DropdownItem({ text: type.name.value, value: type }));
		}
		return dropdownItems;
	}

	#getDummyCustomersFropdownItems(): DropdownItem[] {
		const dropdownItems: DropdownItem[] = [];
		const customers = getCustomers();
		for (const customer of customers) {
			dropdownItems.push(
				new DropdownItem({
					text: customer.name.value,
					value: customer,
				}),
			);
		}
		return dropdownItems;
	}

	/** Opens the Configurator in a new window with Order information added to the URL as query parameters. */
	#openConfigurator() {
		const url = this.router.serializeUrl(this.router.createUrlTree(['/sales/configuration']));

		const queryParameters = this.#getOpenConfiguratorQueryParameters();
		// creates the final URL with appended query parameters
		const openURL = `${url}?${queryParameters}`;
		// properties applied to window we are going to open
		const openParameters = `width=${screen.availWidth},height=${screen.availHeight},top=0,left=0,scrollbars=no,resizable=yes`;
		/** Do not pass noopener/noreferrer here: the new window then starts with an empty sessionStorage and shows the login dialog, because the session (tokens, account) is copied to a new window only when it keeps its opener. Setting opener to null right after opening still prevents reverse-tabnabbing. */
		const newWindow = window.open(openURL, '_blank', openParameters);
		if (newWindow) {
			newWindow.opener = null;
		}
	}

	/**
	 * Returns the orderNumber and debitorNumber as URI encoded key value pairs.
	 *
	 * We use encodeURIComponent() since encodeURI does not encode special characters like & and # URL safe.
	 */
	#getOpenConfiguratorQueryParameters(): string {
		const order = this.currentSalesOrder;
		const orderNumber = encodeURIComponent(order.orderNumber.value);
		const customer = order.customer;
		const debitorNumber = encodeURIComponent(customer.key.value);
		return `orderNumber=${orderNumber}&debitorNumber=${debitorNumber}`;
	}

	public showDialog(): void {
		this.dialog.nativeElement.showModal();
	}
}
